// src/components/admin/StartOpenBiddingWithFinalistsModal.jsx
// Modal combinado: seleccionar finalistas Y configurar subasta abierta

import React, { useState, useEffect, useMemo } from 'react';
import { X, Play, Check, TrendingUp, Clock, AlertTriangle, Calendar } from 'lucide-react';
import { SemaphoreIcon } from '../SemaphoreIndicator';

/**
 * Modal combinado para:
 * 1. Seleccionar finalistas de la fase ciega
 * 2. Configurar precio inicial y duración de subasta abierta
 * 3. Opcionalmente programar inicio diferido
 */
const StartOpenBiddingWithFinalistsModal = ({
  property,
  blindOffers,
  onConfirm,
  onClose
}) => {
  // Solo ofertas verificadas pueden ser seleccionadas
  const verifiedOffers = blindOffers.filter(o => o.isVerified);

  // Estado: IDs de ofertas seleccionadas como finalistas
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Configuración de subasta
  const [startingPrice, setStartingPrice] = useState(0);
  const [startNow, setStartNow] = useState(true); // Iniciar ahora por defecto
  const [biddingStartDate, setBiddingStartDate] = useState(''); // datetime-local format
  const [deadline, setDeadline] = useState(''); // datetime-local format
  const [minIncrement, setMinIncrement] = useState(property.minIncrement || Math.floor(property.startPrice * 0.01 / 500) * 500);
  const [maxBidPercentage, setMaxBidPercentage] = useState(property.maxBidPercentage || 20);

  // Calcular mejor oferta de los seleccionados
  const bestSelectedOffer = useMemo(() => {
    const selectedOffers = verifiedOffers.filter(o => selectedIds.has(o.id));
    if (selectedOffers.length === 0) return property.startPrice;
    return Math.max(...selectedOffers.map(o => o.amount));
  }, [selectedIds, verifiedOffers, property.startPrice]);

  // Función para formatear fecha a datetime-local
  const formatDatetimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Al montar: preseleccionar verdes y establecer fechas por defecto
  useEffect(() => {
    const preselected = new Set();
    verifiedOffers.forEach(offer => {
      if (offer.semaphoreStatus === 'green') {
        preselected.add(offer.id);
      }
    });
    setSelectedIds(preselected);

    // Fecha de inicio: ahora + 1 hora (redondeado a la hora siguiente)
    const now = new Date();
    const startDefault = new Date(now);
    startDefault.setHours(startDefault.getHours() + 1);
    startDefault.setMinutes(0, 0, 0);
    setBiddingStartDate(formatDatetimeLocal(startDefault));

    // Fecha de fin: inicio + 48 horas por defecto
    const endDefault = new Date(startDefault);
    endDefault.setHours(endDefault.getHours() + 48);
    setDeadline(formatDatetimeLocal(endDefault));
  }, []);

  // Actualizar precio inicial cuando cambia la selección
  useEffect(() => {
    setStartingPrice(bestSelectedOffer);
  }, [bestSelectedOffer]);

  const toggleSelection = (offerId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(offerId)) {
        newSet.delete(offerId);
      } else {
        newSet.add(offerId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(verifiedOffers.map(o => o.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleConfirm = () => {
    const selectedOffers = verifiedOffers.filter(o => selectedIds.has(o.id));
    const inviteIds = selectedOffers.map(o => o.inviteId).filter(Boolean);

    // Convertir fechas locales a ISO UTC
    const startDateISO = startNow ? new Date().toISOString() : new Date(biddingStartDate).toISOString();
    const deadlineISO = new Date(deadline).toISOString();

    const config = {
      startingPrice,
      biddingStartDate: startDateISO,
      deadline: deadlineISO,
      minIncrement,
      maxBidPercentage,
      finalistInviteIds: inviteIds,
      selectedOffers
    };

    console.log('🔍 Modal handleConfirm - Config:', config);
    console.log('🔍 selectedOffers:', selectedOffers);
    console.log('🔍 inviteIds:', inviteIds);

    onConfirm(config);
  };

  // Agrupar por semáforo para mostrar
  const greenOffers = verifiedOffers.filter(o => o.semaphoreStatus === 'green');
  const yellowOffers = verifiedOffers.filter(o => o.semaphoreStatus === 'yellow');
  const redOffers = verifiedOffers.filter(o => o.semaphoreStatus === 'red');

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Validar que haya al menos 2 finalistas seleccionados
  const canSubmit = selectedIds.size >= 2;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal - Fullscreen en móvil/tablet, centrado en desktop */}
      <div className="fixed inset-0 z-50 lg:flex lg:items-center lg:justify-center lg:p-4">
        <div
          className="relative bg-slate-800 w-full h-full flex flex-col lg:rounded-2xl lg:shadow-2xl lg:max-w-6xl lg:max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-slate-700">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
                <Play className="w-6 h-6 text-green-400" />
                Abrir Subasta Abierta
              </h2>
              <p className="text-slate-400">
                {property.title} - Selecciona finalistas y configura la subasta
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Contenido - Dividido en dos columnas: 50% / 50% */}
          <div className="p-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* COLUMNA IZQUIERDA: Selección de finalistas (50%) */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">1. Seleccionar Finalistas</h3>

                {/* Ofertas verdes */}
                {greenOffers.length > 0 && (
                  <OfferGroup
                    title="Ofertas Óptimas"
                    offers={greenOffers}
                    selectedIds={selectedIds}
                    onToggle={toggleSelection}
                    formatAmount={formatAmount}
                    bgColor="bg-green-500/5"
                    borderColor="border-green-500/20"
                  />
                )}

                {/* Ofertas amarillas */}
                {yellowOffers.length > 0 && (
                  <OfferGroup
                    title="Ofertas Aceptables"
                    offers={yellowOffers}
                    selectedIds={selectedIds}
                    onToggle={toggleSelection}
                    formatAmount={formatAmount}
                    bgColor="bg-yellow-500/5"
                    borderColor="border-yellow-500/20"
                  />
                )}

                {/* Ofertas rojas */}
                {redOffers.length > 0 && (
                  <OfferGroup
                    title="Ofertas Fuera de juego"
                    offers={redOffers}
                    selectedIds={selectedIds}
                    onToggle={toggleSelection}
                    formatAmount={formatAmount}
                    bgColor="bg-red-500/5"
                    borderColor="border-red-500/20"
                  />
                )}

                {/* Warning si no hay suficientes seleccionadas */}
                {selectedIds.size < 2 && (
                  <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mt-4">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <p className="text-sm text-amber-300">
                      Debes seleccionar al menos 2 finalistas para abrir la subasta.
                    </p>
                  </div>
                )}
              </div>

              {/* COLUMNA DERECHA: Configuración de subasta (50%) */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">2. Configurar Subasta</h3>

                <div className="space-y-4">
                  {/* Info de precios de referencia */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Precio de salida original */}
                    <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700">
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-amber-400" />
                        <span className="text-xs font-medium text-slate-400">Precio de salida</span>
                      </div>
                      <div className="text-xl font-bold text-white pl-5">
                        {formatAmount(property.originalStartPrice || property.startPrice)}
                      </div>
                    </div>
                    {/* Mejor oferta seleccionada */}
                    <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700">
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-slate-400">Mejor oferta seleccionada</span>
                      </div>
                      <div className="text-xl font-bold text-white pl-5">
                        {formatAmount(bestSelectedOffer)}
                      </div>
                    </div>
                  </div>

                  {/* Inicio y Cierre de pujas */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300">
                          Inicio de pujas *
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={startNow}
                            onChange={(e) => setStartNow(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-600 text-green-500 focus:ring-green-500 focus:ring-offset-0 bg-slate-700"
                          />
                          <span className="text-xs text-slate-400">Ahora</span>
                        </label>
                      </div>
                      <input
                        type="datetime-local"
                        value={biddingStartDate}
                        onChange={(e) => setBiddingStartDate(e.target.value)}
                        disabled={startNow}
                        className={`w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500 ${
                          startNow ? 'text-slate-500 opacity-50 cursor-not-allowed' : 'text-white'
                        }`}
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        {startNow ? 'Se iniciará inmediatamente' : 'Cuándo comenzará la subasta abierta'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Cierre de pujas *
                      </label>
                      <input
                        type="datetime-local"
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Cuándo finalizará la subasta abierta
                      </p>
                    </div>
                  </div>

                  {/* Configuración de incrementos */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Incremento mínimo (€)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100000"
                        step="100"
                        value={minIncrement}
                        onChange={(e) => setMinIncrement(parseFloat(e.target.value) || 500)}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                        placeholder="500"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Cantidad mínima de incremento entre pujas
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Incremento máximo por puja (%)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={maxBidPercentage}
                        onChange={(e) => setMaxBidPercentage(parseFloat(e.target.value) || 15)}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                        placeholder="15"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Máximo incremento permitido
                      </p>
                    </div>
                  </div>

                  {/* Información de fechas */}
                  <div className="text-xs text-slate-400 space-y-1 bg-slate-900/30 p-3 rounded-lg">
                    <p>• Debe haber al menos 24 horas entre el inicio y el cierre</p>
                    <p>• Los finalistas serán notificados al inicio de la subasta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-700 bg-slate-800/50">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all"
              >
                Cancelar
              </button>

              {/* Warning centrado */}
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-300">
                  Los finalistas serán notificados por email
                </p>
              </div>

              <button
                onClick={handleConfirm}
                disabled={!canSubmit}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="w-5 h-5" />
                Abrir Subasta ({selectedIds.size} finalistas)
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Grupo de ofertas por estado de semáforo
 */
const OfferGroup = ({ title, offers, selectedIds, onToggle, formatAmount, bgColor, borderColor }) => {
  // Funciones para seleccionar/deseleccionar todas las ofertas de este grupo
  const selectAllInGroup = () => {
    offers.forEach(offer => {
      if (!selectedIds.has(offer.id)) {
        onToggle(offer.id);
      }
    });
  };

  const deselectAllInGroup = () => {
    offers.forEach(offer => {
      if (selectedIds.has(offer.id)) {
        onToggle(offer.id);
      }
    });
  };

  return (
    <div className={`mb-3 p-3 rounded-lg ${bgColor} border ${borderColor}`}>
      {/* Header con título y acciones */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-300">{title} ({offers.length})</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={selectAllInGroup}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Todas
          </button>
          <span className="text-slate-600">|</span>
          <button
            onClick={deselectAllInGroup}
            className="text-xs text-slate-400 hover:text-slate-300"
          >
            Ninguna
          </button>
        </div>
      </div>

      {/* Lista de ofertas */}
      <div className="space-y-1">
        {offers.map(offer => (
          <OfferRow
            key={offer.id}
            offer={offer}
            isSelected={selectedIds.has(offer.id)}
            onToggle={() => onToggle(offer.id)}
            formatAmount={formatAmount}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Fila individual de oferta
 */
const OfferRow = ({ offer, isSelected, onToggle, formatAmount }) => (
  <div
    onClick={onToggle}
    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
      isSelected
        ? 'bg-green-500/20 border border-green-500/50'
        : 'bg-slate-800/50 border border-transparent hover:border-slate-600'
    }`}
  >
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {/* Checkbox */}
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
        isSelected
          ? 'bg-green-500 border-green-500'
          : 'border-slate-500'
      }`}>
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Semáforo */}
      <div className="flex-shrink-0">
        <SemaphoreIcon status={offer.semaphoreStatus} size="sm" />
      </div>

      {/* Info - Nombre y email en la misma línea */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-white font-medium text-sm truncate">{offer.buyerName}</span>
        <span className="text-xs text-slate-400 truncate">{offer.buyerEmail}</span>
      </div>
    </div>

    {/* Monto */}
    <div className="text-base font-bold text-white flex-shrink-0">
      {formatAmount(offer.amount)}
    </div>
  </div>
);

export default StartOpenBiddingWithFinalistsModal;
